import { ColDef, GetQuickFilterTextParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import { useMemo } from 'react';
import { FieldPath } from 'react-hook-form';
import { ICompanyDataModel } from '../../../../../data-models/company.data-model';
import { IMetricsDataModel } from '../../../../../data-models/metrics.data-model';
import {
  getOrgPreferenceKey,
  PreferenceKey,
  PreferenceScope,
} from '../../../../../data-models/preferences-v3.data-model';
import { PreferencesServiceV3 } from '../../../../../util/preferences-service-v3';
import { useCompanyMapLookup } from '../../../../../services/hooks/useSaveCompanyData';

export function usePortCoColDefs(): ColDef {
  const getCompany = useCompanyMapLookup();

  return useMemo(() => {
    return {
      field: 'companyId',
      valueGetter: (params: ValueGetterParams<IMetricsDataModel>) => {
        const companyId = params.data?.companyId;
        const company = getCompany(companyId ?? -1);
        return portcoValue(company);
      },
      valueFormatter: (params: ValueFormatterParams<IMetricsDataModel>) => {
        return portcoFormatter(params.value);
      },
      comparator: (valueA?: string[], valueB?: string[]) => {
        return portcoFormatter(valueA).localeCompare(portcoFormatter(valueB), undefined, {
          sensitivity: 'base',
        });
      },
      filterValueGetter: (params: ValueGetterParams<IMetricsDataModel>) => {
        return portcoValue(getCompany(params.data?.companyId ?? -1));
      },

      getQuickFilterText: (
        params: GetQuickFilterTextParams<IMetricsDataModel, string[] | null | undefined>
      ) => {
        const companyId = params.data?.companyId;
        const company = getCompany(companyId ?? -1);
        return portcoQuickFilterTextGetter(company, params.context?.quickFilterPrefs);
      },
    };
  }, [getCompany]);
}

export function portcoFormatter(value?: string[]) {
  if (!Array.isArray(value) || !value?.length) {
    return '';
  }
  return value?.at(0) ?? '';
}

export function portcoValue(company: ICompanyDataModel | null) {
  if (!company?.name) {
    return null;
  }
  const result = [company.name];
  const acquirerName = company.acquirer?.fields?.name;
  if (acquirerName) {
    result.push(acquirerName);
  }
  return result;
}

export const defaultQuickSearchColumns: FieldPath<ICompanyDataModel>[] = ['name', 'acquirer.fields.name'];

export function portcoQuickFilterTextGetter(
  company: ICompanyDataModel | null | undefined,
  prefs?: Set<FieldPath<ICompanyDataModel>>
): string {
  const res = [];
  for (const column of prefs ?? defaultQuickSearchColumns) {
    const value = get(company, column);
    if (Array.isArray(value) && value.length > 0) {
      // assumes array values are strings
      res.push(...value);
    } else if (value != null) {
      res.push(value);
    }
  }
  return res.join(' ');
}

export function getQuickSearchColumnsFromPrefs(): Set<FieldPath<ICompanyDataModel>> {
  const prefs = PreferencesServiceV3.get().getPreference<FieldPath<ICompanyDataModel>[]>(
    getOrgPreferenceKey(PreferenceKey.portfolioOverviewQuickSearchColumns, null),
    PreferenceScope.ORGANIZATION
  );
  return new Set<FieldPath<ICompanyDataModel>>(prefs ?? defaultQuickSearchColumns);
}

export function getQuickSearchPlaceholderText(): string {
  const prefs = getQuickSearchColumnsFromPrefs();
  if (prefs.has('name') && prefs.has('acquirer.fields.name')) {
    return 'Search by company or acquirer';
  }
  if (prefs.has('name')) {
    return 'Search by company';
  }
  return 'Search';
}
